<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Message from 'primevue/message';
import { useToast } from 'primevue/usetoast';
import { adminAPI } from '../service/api.js';

const props = defineProps({
  visible: { type: Boolean, required: true },
});
const emit = defineEmits(['update:visible', 'created']);

const toast = useToast();

const tenant = ref('');
const description = ref('');
const submitting = ref(false);

// `result` holds the one-shot reveal payload after a successful create.
const result = ref(null);
const copiedId = ref(false);
const copiedSecret = ref(false);

const reset = () => {
  tenant.value = '';
  description.value = '';
  submitting.value = false;
  result.value = null;
  copiedId.value = false;
  copiedSecret.value = false;
};

// Wipe state every time the dialog reopens so a previous secret can't
// linger on a re-open.
watch(() => props.visible, (now) => {
  if (now) reset();
});

const submit = async () => {
  if (!tenant.value.trim()) return;
  submitting.value = true;
  try {
    const response = await adminAPI.createApiClient({
      tenant: tenant.value.trim(),
      description: description.value.trim(),
    });
    result.value = response.data;
    emit('created');
  } catch (e) {
    console.error('createApiClient failed:', e);
    toast.add({
      severity: 'error',
      summary: 'Failed to create client',
      detail: e?.response?.data || e?.message || 'Unknown error',
      life: 5000,
    });
  } finally {
    submitting.value = false;
  }
};

const copy = async (text, kind) => {
  try {
    await navigator.clipboard.writeText(text);
    if (kind === 'id') copiedId.value = true;
    if (kind === 'secret') copiedSecret.value = true;
    toast.add({ severity: 'success', summary: 'Copied', life: 1500 });
  } catch (e) {
    toast.add({
      severity: 'warn',
      summary: 'Copy failed — select and copy manually',
      life: 3000,
    });
  }
};

const close = () => {
  emit('update:visible', false);
};
</script>

<template>
  <Dialog
    :visible="visible"
    @update:visible="(v) => emit('update:visible', v)"
    modal
    :closable="false"
    :close-on-escape="false"
    :dismissable-mask="false"
    :style="{ width: '32rem' }"
    :header="result ? 'Save these credentials' : 'New API client'"
  >
    <!-- PHASE 1: form -->
    <div v-if="!result" class="form-body">
      <div class="form-group">
        <label for="new-tenant">Tenant <span class="required">*</span></label>
        <InputText
          id="new-tenant"
          v-model="tenant"
          placeholder="e.g. PENSER"
          autofocus
        />
      </div>
      <div class="form-group">
        <label for="new-description">Description</label>
        <InputText
          id="new-description"
          v-model="description"
          placeholder="e.g. Terra production"
        />
        <small class="help">Free-form. Include the partner name and environment.</small>
      </div>

      <div class="dialog-actions">
        <Button label="Cancel" severity="secondary" text @click="close" />
        <Button
          label="Create"
          icon="pi pi-plus"
          :loading="submitting"
          :disabled="!tenant.trim()"
          @click="submit"
        />
      </div>
    </div>

    <!-- PHASE 2: one-shot reveal -->
    <div v-else class="reveal-body">
      <Message severity="warn" :closable="false" class="warn-banner">
        The client secret is shown <strong>only once</strong>. Copy it now —
        if you lose it, you must rotate this client to issue new credentials.
      </Message>

      <div class="cred-row">
        <label>Tenant</label>
        <div class="cred-value">{{ result.tenant }}</div>
      </div>

      <div class="cred-row">
        <label>Client ID</label>
        <div class="cred-value mono">
          <span class="cred-text">{{ result.client_id }}</span>
          <Button
            :icon="copiedId ? 'pi pi-check' : 'pi pi-copy'"
            severity="secondary"
            text
            size="small"
            @click="copy(result.client_id, 'id')"
            aria-label="Copy client_id"
          />
        </div>
      </div>

      <div class="cred-row">
        <label>Client Secret</label>
        <div class="cred-value mono secret">
          <span class="cred-text">{{ result.client_secret }}</span>
          <Button
            :icon="copiedSecret ? 'pi pi-check' : 'pi pi-copy'"
            severity="secondary"
            text
            size="small"
            @click="copy(result.client_secret, 'secret')"
            aria-label="Copy client_secret"
          />
        </div>
      </div>

      <div class="dialog-actions">
        <Button
          label="I've copied these credentials"
          icon="pi pi-check"
          :disabled="!copiedSecret"
          @click="close"
        />
      </div>
      <small v-if="!copiedSecret" class="help">
        The button enables after you click the secret's copy icon.
      </small>
    </div>
  </Dialog>
</template>

<style scoped>
.form-body, .reveal-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  text-align: left;
}
.form-group label, .cred-row label {
  font-weight: 500;
  color: #444;
  font-size: 0.875rem;
}
.required {
  color: #b91c1c;
}
.help {
  color: #777;
  font-size: 0.75rem;
}
.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
}
.warn-banner {
  margin: 0;
}
.cred-row {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  text-align: left;
}
.cred-value {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 0.5rem;
}
.cred-text {
  flex: 1;
  word-break: break-all;
}
.mono {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.875rem;
}
.secret {
  background: #fef9c3;
  border-color: #fde047;
}
</style>
